import React from 'react';
import {
  Button,
  Grid,
  Header,
  Label,
  Divider,
  SemanticCOLORS,
  Icon,
} from 'semantic';
import ThemedSegment from 'components/semantic/Segment';
import { TFunction, useTranslation } from 'react-i18next';
import {
  TariffRule as CustomTariffRuleType,
  TariffRule,
} from 'subdomains/cpo/types/charging-policy';
import { getCombinedPriceAndRestrictionsString } from 'subdomains/cpo/screens/PoliciesAndGroups/Tariff/TariffComponentPreview';
import { formatDateForDisplay } from 'subdomains/cpo/screens/PoliciesAndGroups/Tariff/Preview';
import ConfirmDelete from 'components/ConfirmDelete';

type Props = {
  values: CustomTariffRuleType;
  currency: string;
  onEdit?: () => void;
  onRemove?: () => Promise<void> | undefined;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
};

export const TariffRulePreview: React.FC<Props> = ({
  values,
  currency,
  onEdit,
  onRemove,
  dragHandleProps,
}) => {
  const { t } = useTranslation();

  const { status: validityStatus, color: validityColor } =
    getValidityPeriodStatus(values.validityPeriod, t);

  return (
    <ThemedSegment offbackground style={{ paddingBottom: '1.5rem' }}>
      <Grid columns="equal" verticalAlign="middle">
        <Grid.Row centered verticalAlign="middle">
          <Grid.Column
            width={6}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
            <div {...dragHandleProps} style={{ display: 'flex' }}>
              <Icon size="large" name="arrows-up-down-left-right" />
            </div>
            <h3 style={{ margin: '0 0 0 .8rem' }}>
              {t('tariff.component.tariff', 'Tariff')}
            </h3>

            {validityStatus && validityStatus !== 'Active' && (
              <Label
                basic
                size="large"
                color={validityColor}
                style={{ marginLeft: '1.5rem' }}>
                {validityStatus}
              </Label>
            )}
          </Grid.Column>

          <Grid.Column
            floated="right"
            width={2}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexDirection: 'row',
            }}>
            {onEdit && (
              <Button icon="edit" basic size="mini" onClick={onEdit} />
            )}
            {onRemove && (
              <ConfirmDelete
                onConfirm={onRemove}
                name={t(
                  'chargingPolicy.pricing.tariffRule.deletePrompt.theTariff',
                  'the tariff'
                )}
                objectName={t(
                  'chargingPolicy.pricing.tariffRule.deletePrompt.tariff',
                  'tariff'
                )}
                trigger={<Button icon="trash" basic size="mini" />}
              />
            )}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
          <Grid.Column width={3}>
            <p>
              {t(
                'chargingPolicy.pricing.tariffRulePreview.authorizationMethods',
                'Payment options:'
              )}
            </p>
          </Grid.Column>
          <Grid.Column>
            {values.authorizationMethods?.length ? (
              values.authorizationMethods?.map((value) => (
                <Label
                  basic
                  size="large"
                  content={t(
                    `chargingPolicy.pricing.authorizationMethods.${value.toLowerCase()}`,
                    value
                  )}
                />
              ))
            ) : (
              <Label
                basic
                size="large"
                content={t(
                  `chargingPolicy.pricing.authorizationMethods.allPaymentOptions`,
                  'All payment options'
                )}
              />
            )}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
          <Grid.Column width={3}>
            <p>
              {t(
                'chargingPolicy.pricing.tariffRulePreview.appliesTo',
                'Applies to:'
              )}
            </p>
          </Grid.Column>
          <Grid.Column
            style={{ display: 'flex', gap: '1rem', flexDirection: 'row' }}>
            {/*TODO: we need the names of the groups rather than the IDs*/}
            {values.customerGroups?.length ? (
              values.customerGroups.map((value) => (
                <CustomerGroupLabel color="blue" content={value} />
              ))
            ) : (
              <CustomerGroupLabel
                color={'black'}
                content={t(
                  'chargingPolicy.pricing.tariffRulePreview.everyone',
                  'Everyone'
                )}
              />
            )}
          </Grid.Column>
        </Grid.Row>
        <Divider />
        <Grid.Row style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
          <Grid.Column width={3}>
            <p>
              {t(
                'chargingPolicy.pricing.tariffRulePreview.tariffComponents',
                'Tariff:'
              )}
            </p>
          </Grid.Column>
          <Grid.Column>
            {values.tariff?.components?.map((component) => (
              <Label
                basic
                size="large"
                content={getCombinedPriceAndRestrictionsString(
                  component,
                  currency,
                  t
                )}
              />
            ))}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
          <Grid.Column width={3}>
            <p>{t('tariff.component.descriptions', 'Description:')}</p>
          </Grid.Column>
          <Grid.Column>
            {values.tariff?.descriptions?.map((description) => (
              <p>
                {description.language?.toUpperCase()}: {description.text}
              </p>
            ))}
          </Grid.Column>
        </Grid.Row>
        {values.validityPeriod?.enabled && (
          <Grid.Row style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
            <Grid.Column width={3}>
              <p>{t('tariff.component.valid', 'Valid:')}</p>
            </Grid.Column>
            <Grid.Column>
              {values.validityPeriod && (
                <Label basic size="large">
                  {validityStatus !== 'Active' && (
                    <Icon name="circle-exclamation" color="red" />
                  )}
                  {formatValidityPeriod(values.validityPeriod)}
                </Label>
              )}
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </ThemedSegment>
  );
};

const CustomerGroupLabel = ({
  content,
  color,
}: {
  content: string;
  color: SemanticCOLORS;
}) => {
  return (
    <Header style={{ margin: 0 }} as="h5" color={color} content={content} />
  );
};

export function formatValidityPeriod(
  date: NonNullable<TariffRule['validityPeriod']>
): string {
  if (!date?.enabled || (!date.start && !date.end)) return '';

  if (date.start && date.end) {
    return `${formatDateForDisplay(date.start)}-${formatDateForDisplay(date.end)}`;
  } else if (date.start) {
    return `From ${formatDateForDisplay(date.start)}`;
  } else if (date.end) {
    return `Until ${formatDateForDisplay(date.end)}`;
  }
  return '';
}

function getValidityPeriodStatus(
  validityPeriod: CustomTariffRuleType['validityPeriod'],
  t: TFunction<'translation', undefined>
): { status: string; color: SemanticCOLORS } {
  if (!validityPeriod || !validityPeriod.enabled) {
    return { status: '', color: 'red' };
  }

  const { start, end } = validityPeriod;

  const now = new Date();
  let status = 'Active';
  let color: SemanticCOLORS = 'green';
  if (start && now < start) {
    status = t('tariff.component.notActiveYet', 'Not active yet');
    color = 'orange';
  }
  if (end && now > end) {
    status = t('tariff.component.expired', 'Expired');
    color = 'red';
  }

  return { status, color };
}

// function formatTariff(
//   tariff: Tariff,
//   t: TFunction<'translation', undefined>
// ): React.ReactNode {
//   if (!tariff || !tariff.components || tariff.components.length === 0) {
//     return '';
//   }
//   return (
//     <>
//       {tariff.components.map((component, index) => (
//         <React.Fragment key={index}>
//           {getCombinedPriceAndRestrictionsString(component, 'EUR', t)}
//           {index < tariff.components.length - 1 && <br />}
//         </React.Fragment>
//       ))}
//     </>
//   );
// }
